import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Skeleton } from "@material-ui/lab";
import companyPublicView from "../../../services/companyPublicView.services";
import _ from "lodash";
import appRoutes from "../../../routes/app.routes";
import { Button } from "../../../components/common";
import FeedList from "../../Feeds/FeedList";
import ArrowUp from "../../../components/ProfileViewNew/icons/arrow-up.svg";
import navigate from "../../../utils/history";

type FeedProps = {
  loading?: boolean;
};

const PREVIEW_LIMIT = 3;

const Feed = ({ loading: profileLoading = false }: FeedProps) => {
  const location = useLocation();
  const [feeds, setFeeds] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [feedsLoading, setFeedsLoading] = useState(true);
  const { companyPublicProfile } = useSelector(
    ({ companyPublicView }: any) => companyPublicView.companyPublicView
  );

  const headerUser = useMemo(
    () => ({
      name: _.get(companyPublicProfile, "companyProfile.company_name", ""),
      profile_image: _.get(companyPublicProfile, "companyProfile.company_logo", ""),
      isCompany: true,
    }),
    [companyPublicProfile]
  );

  useEffect(() => {
    let cancelled = false;

    const loadFeeds = async () => {
      const companyId = companyPublicProfile?.id;

      if (!companyId) {
        if (profileLoading) {
          setFeedsLoading(true);
          return;
        }
        if (!cancelled) {
          setFeeds([]);
          setTotalRecords(0);
          setFeedsLoading(false);
        }
        return;
      }

      setFeedsLoading(true);
      try {
        const res: any = await companyPublicView.getCompanyFeeds({
          companyId,
          page: 1,
          pageSize: PREVIEW_LIMIT,
          postType: 0,
        });
        if (cancelled) return;
        const raw =
          res?.flag === false ? [] : _.get(res, "data", []);
        const list = Array.isArray(raw) ? raw : [];
        setFeeds(list);
        setTotalRecords(_.get(res, "pagination.totalRecords", 0));
      } catch {
        if (!cancelled) {
          setFeeds([]);
          setTotalRecords(0);
        }
      } finally {
        if (!cancelled) setFeedsLoading(false);
      }
    };

    loadFeeds();

    return () => {
      cancelled = true;
    };
  }, [companyPublicProfile?.id, profileLoading, location.key]);

  const isLoading = profileLoading || feedsLoading;
  const companySlug = window.location.pathname.split("/")[2];
  const showSeeMore = totalRecords > PREVIEW_LIMIT;

  const handleSeeMore = () => {
    navigate(appRoutes.companyPostsList.generatePath(companySlug));
  };

  if (isLoading) {
    return (
      <div className="company-home-section company-feed-section">
        <div className="company-feed-list">
          {[1, 2, 3].map((item) => (
            <Skeleton
              key={`feed-skeleton-${item}`}
              variant="rect"
              width="100%"
              height={160}
              style={{ marginBottom: 16, borderRadius: 8 }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (_.isEmpty(feeds)) {
    return null;
  }

  return (
    <div className="company-home-section  company-feed-section">
      <div className="company-feed-list">
        <FeedList
          readOnly={true}
          feeds={feeds}
          headerUser={headerUser}
          fetchOnScroll={false}
          pageSize={PREVIEW_LIMIT}
        />
        {showSeeMore && (
          <div className="btn-wrapper">
            <Button className="all-services-btn" onClick={handleSeeMore}>
              <span>See more</span>
              <img src={ArrowUp} alt="Arrow" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Feed;
